// @ts-check
import React, { useState, useEffect, useCallback } from 'react';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import addDays from 'date-fns/addDays';
import formatISO from 'date-fns/formatISO';
import lightFormat from 'date-fns/lightFormat';
import FuturePortalLayout from '../../layouts/FuturePortalLayout';
// import ControlPanel from './ControlPanel';
// import SidePanel from './SidePanel';
import { LineChartStat } from 'admin_portal/components/EntityReferralStats/types'; // eslint-disable-line no-unused-vars
import LoadingContent from 'shared/components/LoadingContent';
import api from 'utils/api';

import { Histogram } from './Histogram';
import TwoLevelPieChart from './TwoLevelPieChart';
// import SimpleBarChart from './SimpleBarChart';
import './AnalyticsPage.css';
import LineChart from './charts/LineChart';
import { FilterPanel, FilterModel } from './FilterPanel'; // eslint-disable-line no-unused-vars
import { sortBy } from 'lib/utils';
import { FAIcon } from 'preferral-ui/components/FAIcon';
import { TableOrVerticalBarChart } from './charts/TableOrVerticalBarChart';
import { DataExportModal } from './DataExportModal';
import { Button } from 'preferral-ui/components/Button';

const NoData = () => {
  return (
    <div className="_NoData text-center">
      <p
        className="bg-gray-50 inline-block mx-auto rounded-lg shadow-inner"
        style={{ padding: '1rem 2rem' }}
      >
        <span style={{ marginRight: '0.5rem' }}>
          <FAIcon icon="chart-bar" />
        </span>{' '}
        No data for query
      </p>
    </div>
  );
};

/**
 * @param {LineChartStat[]} statSet
 * @returns {number}
 */
const totalNumReferrals = (statSet) =>
  statSet.reduce((acc, elem) => acc + elem.numReferrals, 0);

const startOfLastWeek = startOfWeek(addDays(new Date(), -7));
const endOfLastWeek = endOfWeek(addDays(new Date(), -7));

/** @type { FilterModel } */
const defaultFilter = {
  dateRange: {
    start: startOfLastWeek,
    finish: endOfLastWeek,
  },
  locationId: 'ALL',
};

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(defaultFilter);
  const { dateRange, locationId } = filter;

  const timeRangeKey = `${isoFormatDate(dateRange.start)} to ${isoFormatDate(
    dateRange.finish
  )}`;
  const rangeString = `${mmDdYyyy(dateRange.start)} through ${mmDdYyyy(
    dateRange.finish
  )}`;

  const [activeModal, setActiveModal] = useState(null);
  const closeModal = useCallback(() => setActiveModal(null), []);

  useEffect(() => {
    setLoading(true);

    const params = {
      start: isoFormatDate(dateRange.start),
      finish: isoFormatDate(dateRange.finish),
    };

    if (locationId !== 'ALL') {
      params.location_id = locationId;
    }

    api.get('/agent_portal/api/referral_stats', { params }).then((response) => {
      setData(response.data.referralStats);
      setLoading(false);
      setLoaded(true);
    });
  }, [timeRangeKey, locationId]);

  return (
    <FuturePortalLayout title="Analytics">
      <DataExportModal
        isOpen={activeModal === 'DATA_EXPORT'}
        onClose={closeModal}
        filter={filter}
      />
      <div style={{ padding: '1.5rem' }}>
        {loading || !loaded ? (
          <LoadingContent />
        ) : (
          <div>
            <section style={{ padding: '1rem' }}>
              <div className="box flex items-center justify-around">
                <div className="_flex-placeholder" />
                <FilterPanel value={filter} onChange={setFilter} />
                {/* <div className="mx-auto text-center">
                  <DateRangeDropdown value={dateRange} onChange={setDateRange} />
                </div> */}
                <div>
                  <Button onClick={() => setActiveModal('DATA_EXPORT')}>
                    Export
                  </Button>
                </div>
              </div>
            </section>
            <section style={{ padding: '1rem' }}>
              <div className="box">
                <div style={{ position: 'relative' }}>
                  <div className="big_stat_container">
                    <div>
                      <h4 className="px-3 text-lg font-semibold text-center">
                        All Referrals
                      </h4>
                      <div className="flex items-center">
                        <div className="big_stat">
                          <h1>{data.totalReferred}</h1>
                          <p>Total Sent</p>
                        </div>
                        <div className="big_stat">
                          <h1>{data.totalReceived}</h1>
                          <p>Total Received</p>
                        </div>
                      </div>
                    </div>
                    <div className="pl-3 ml-3 border-l-2 border-gray-300">
                      <h4 className="px-3 text-lg font-semibold text-center">
                        Received Referrals
                      </h4>
                      <div className="flex items-center">
                        <div className="big_stat">
                          <h1>{data.totalReceivedScheduled}</h1>
                          <p>Scheduled</p>
                        </div>
                        <div className="big_stat">
                          <h1>{data.totalReceivedExhausted}</h1>
                          <p>Exhausted</p>
                        </div>
                        <div className="big_stat">
                          <h1>{data.totalReceivedRejected}</h1>
                          <p>Rejected</p>
                        </div>
                      </div>
                    </div>
                    {/* {dataExportEndpoint && (
                <div className={styles.export_section}>
                  <button className="button is-link" onClick={this.fetchDataExport}>Download Export</button>
                </div>
              )} */}
                  </div>
                </div>
              </div>
            </section>
            <section style={{ padding: '1rem' }}>
              <div className="columns">
                <div className="column">
                  <div className="box">
                    <h3>Sent Referrals ({rangeString})</h3>
                    <h4>
                      Total:{' '}
                      {totalNumReferrals(
                        data.duringRange.referredPerTimeBucket
                      )}
                    </h4>
                    <LineChart
                      label="Sent Referrals"
                      data={data.duringRange.referredPerTimeBucket}
                    />
                  </div>
                </div>
                <div className="column">
                  <div className="box">
                    <h3>Received Referrals ({rangeString})</h3>
                    <h4>
                      Total:{' '}
                      {totalNumReferrals(
                        data.duringRange.receivedPerTimeBucket
                      )}
                    </h4>
                    <LineChart
                      label="Received Referrals"
                      data={data.duringRange.receivedPerTimeBucket}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section style={{ padding: '1rem' }}>
              <div className="columns">
                <div className="column">
                  <div className="box">
                    <h3>Who scheduled appointments?</h3>
                    <p>
                      Appointments scheduled during the time range, counted by
                      who scheduled the appointment
                    </p>
                    {data.duringRange.scheduledAppointmentsPerScheduler
                      .length === 0 ? (
                      <NoData />
                    ) : (
                      <TableOrVerticalBarChart
                        data={sortBy(
                          data.duringRange.scheduledAppointmentsPerScheduler,
                          'count'
                        ).reverse()}
                        labelKey="actorName"
                        valueKey="count"
                        labelName="Team Member"
                        valueName="# Scheduled Appts"
                      />
                    )}
                  </div>
                </div>

                <div className="column">
                  <div className="box">
                    <h3>Who were appointments scheduled with?</h3>
                    <p>
                      Appointments scheduled during the time range, counted by
                      provider
                    </p>
                    {data.duringRange.scheduledAppointmentsPerScheduler
                      .length === 0 ? (
                      <NoData />
                    ) : (
                      <TableOrVerticalBarChart
                        data={sortBy(
                          data.duringRange.scheduledAppointmentsPerProvider,
                          'count'
                        ).reverse()}
                        labelKey="providerName"
                        valueKey="count"
                        labelName="Provider Name"
                        valueName="# Appts"
                      />
                    )}
                  </div>
                </div>

                <div className="column">
                  <div className="box">
                    <h3>Where were appointments scheduled?</h3>
                    <p>
                      Appointments scheduled during the time range, counted by
                      location
                    </p>
                    {data.duringRange.scheduledAppointmentsPerScheduler
                      .length === 0 ? (
                      <NoData />
                    ) : (
                      <TableOrVerticalBarChart
                        data={sortBy(
                          data.duringRange.scheduledAppointmentsPerLocation,
                          'count'
                        ).reverse()}
                        labelKey="locationName"
                        valueKey="count"
                        labelName="Location Name"
                        valueName="# Appts"
                      />
                    )}
                  </div>
                </div>
              </div>
            </section>
            <section style={{ padding: '1rem' }}>
              <div className="columns">
                <div className="column">
                  <div className="box">
                    <h3>Where do our referrals come from?</h3>
                    <p>Received referrals, ranked by referring provider</p>
                    <table
                      className="bulma__table"
                      style={{ margin: '0 auto' }}
                    >
                      <thead>
                        <tr>
                          <th>Provider Name</th>
                          <th># Referrals</th>
                        </tr>
                      </thead>
                      <tbody
                        key={`table-receivedPerReferringProvider-${timeRangeKey}`}
                      >
                        {data.duringRange.receivedPerReferringProvider
                          .sort((a, b) => b.count - a.count)
                          .map((provStat) => (
                            <tr key={provStat.providerId}>
                              <td>{provStat.providerName}</td>
                              <td>{provStat.count}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    <TwoLevelPieChart
                      data={data.duringRange.receivedPerReferringProvider.map(
                        (elem) => ({
                          name: elem.providerName,
                          value: elem.count,
                        })
                      )}
                    />
                  </div>
                </div>
                <div className="column">
                  <div className="box">
                    <h3>Where do we send our referrals?</h3>
                    <p>Sent referrals, ranked by receiving provider</p>

                    <table
                      className="bulma__table"
                      style={{ margin: '0 auto' }}
                    >
                      <thead>
                        <tr>
                          <th>Provider Name</th>
                          <th># Referrals</th>
                        </tr>
                      </thead>
                      <tbody
                        key={`table-referredPerReceivingProvider-${timeRangeKey}`}
                      >
                        {data.duringRange.referredPerReceivingProvider
                          .sort((a, b) => b.count - a.count)
                          .map((provStat) => (
                            <tr key={provStat.providerId}>
                              <td>{provStat.providerName}</td>
                              <td>{provStat.count}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    <TwoLevelPieChart
                      data={data.duringRange.referredPerReceivingProvider.map(
                        (elem) => ({
                          name: elem.providerName,
                          value: elem.count,
                        })
                      )}
                    />
                  </div>
                </div>
              </div>
            </section>

            <section style={{ padding: '1rem' }}>
              <div className="columns">
                <div className="column">
                  <div className="box">
                    <h3>Which of our providers receive the most referrals?</h3>
                    <p>Received referrals, ranked by receiving provider</p>

                    <table
                      className="bulma__table"
                      style={{ margin: '0 auto' }}
                    >
                      <thead>
                        <tr>
                          <th>Provider Name</th>
                          <th># Referrals</th>
                        </tr>
                      </thead>
                      <tbody
                        key={`table-receivedPerReceivingProvider-${timeRangeKey}`}
                      >
                        {data.duringRange.receivedPerReceivingProvider
                          .sort((a, b) => b.count - a.count)
                          .map((provStat) => (
                            <tr key={provStat.providerId}>
                              <td>{provStat.providerName}</td>
                              <td>{provStat.count}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    <TwoLevelPieChart
                      data={data.duringRange.receivedPerReceivingProvider.map(
                        (elem) => ({
                          name: elem.providerName,
                          value: elem.count,
                        })
                      )}
                    />
                  </div>
                </div>
                <div className="column">
                  <div className="box">
                    <h3>Which of our providers send the most referrals?</h3>
                    <p>Sent referrals, ranked by referring provider</p>

                    <table
                      className="bulma__table"
                      style={{ margin: '0 auto' }}
                    >
                      <thead>
                        <tr>
                          <th>Provider Name</th>
                          <th># Referrals</th>
                        </tr>
                      </thead>
                      <tbody
                        key={`table-referredPerReferringProvider-${timeRangeKey}`}
                      >
                        {data.duringRange.referredPerReferringProvider
                          .sort((a, b) => b.count - a.count)
                          .map((provStat) => (
                            <tr key={provStat.providerId}>
                              <td>{provStat.providerName}</td>
                              <td>{provStat.count}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    <TwoLevelPieChart
                      data={data.duringRange.referredPerReferringProvider.map(
                        (elem) => ({
                          name: elem.providerName,
                          value: elem.count,
                        })
                      )}
                    />
                  </div>
                </div>
              </div>
            </section>

            <section style={{ padding: '1rem' }}>
              <div className="box">
                <h3>How quickly do we schedule our received referrals?</h3>
                <p>
                  Scheduled received referrals, binned by how much time passed
                  between when the referral was received and when it was
                  scheduled.
                </p>
                <div style={{ height: 400 }}>
                  {data.duringRange.receivedByHoursUntilScheduled.length > 0 ? (
                    <Histogram
                      binnedData={
                        data.duringRange.receivedByHoursUntilScheduled
                      }
                    />
                  ) : (
                    <p
                      style={{
                        fontSize: 16,
                        padding: '2rem',
                        textAlign: 'center',
                      }}
                    >
                      Not enough data to display
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </FuturePortalLayout>
  );
};

export default AnalyticsPage;

function isoFormatDate(date) {
  return formatISO(date, { representation: 'date' });
}

function mmDdYyyy(date) {
  return lightFormat(date, 'M/d/yy');
}

// fetchDataExport(view) {
//   const { dataExportEndpoint } = this.props;
//   const { activeView } = this.state;
//   if (!!dataExportEndpoint) {
//     return api.get(dataExportEndpoint, { params: { view: activeView }, responseType: 'blob' }).then(response => {
//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement('a');
//       link.href = url;
//       link.setAttribute('donwload', 'file.pdf');
//       document.body.appendChild(link);
//       link.click();
//       console.log(response);
//     });
//   }
//   return null;
// }
